/*
 * landing-price-format.js (AB#8395)
 *
 * Client-side currency converter + formatter for landing-page price spans
 * rendered by the `format-landing-price` snippet.
 *
 * Why client-side and NOT `cart.currency.rate` SSR:
 *   `cart.currency.rate` returns 1 for currencies that are not active
 *   presentment currencies in Shopify Markets (COP, ARS, MXN, etc. in the
 *   Hola store). That made the previous SSR approach mathematically a no-op
 *   for those currencies — "el valor sigue en dólar".
 *
 * Source of truth for rates:
 *   `window.Currency.rates` populated by
 *   <script src="https://cdn.shopify.com/s/javascripts/currencies.js">
 *   loaded in `theme.liquid`. Same file that `Architecture` parses
 *   server-side via `ExchangeShopifyClient`, so the rates are consistent
 *   with `/availability` and the rest of the site.
 *
 * Formatting:
 *   `Intl.NumberFormat({style:'currency',currencyDisplay:'narrowSymbol'})`
 *   so COP/MXN/USD render as "$" instead of "COP$" or "MX$".
 *   AB#9303 — the symbol is ALWAYS rendered before the amount (see
 *   `formatSymbolFirst`); `Intl` would otherwise place it after the number for
 *   es/es-ES/de/fr/it locales ("47.266 $", "14,49 $"). Locale digit grouping /
 *   decimal separators are preserved.
 *   "<span data-amount='28.50' data-currency='USD'>"
 *     -> "$28.50"      (USD)
 *     -> "$120.500"    (COP via Currency.convert)
 *     -> "€24,15"      (EUR via Currency.convert)
 *
 * Re-runnable:
 *   `window.HolaLandingPriceFormat.formatAll(targetCurrency)` formats every
 *   `.landing-price` span on the page with the given target currency and
 *   marks them with `data-formatted="<targetCurrency>"` so subsequent calls
 *   only touch spans whose target moved.
 *
 * Defensive against `window.Currency` not yet loaded (cold cache + defer):
 *   if rates are missing the call is enqueued and retried on the script's
 *   `load` event, plus a final 50ms polling loop with a 2s ceiling. The CSS
 *   guard in theme.liquid keeps spans hidden until conversion completes.
 *
 * Public API:
 *   window.HolaLandingPriceFormat.formatAll(targetCurrency?)
 *   window.HolaLandingPriceFormat.isLandingPriceConvertible(currency)
 *   window.HolaLandingPrice  ← legacy alias for `countries-currency.liquid:110`
 */
(function () {
  'use strict';

  var BASE_CURRENCY = 'USD';
  var POLL_INTERVAL_MS = 50;
  var POLL_TIMEOUT_MS = 2000;
  var pendingTarget = null;
  var pollStart = 0;

  function getLocale() {
    var lang = (document.documentElement && document.documentElement.lang) || '';
    return lang || 'en-US';
  }

  function ratesReady() {
    return (
      typeof window.Currency !== 'undefined' &&
      window.Currency.rates &&
      typeof window.Currency.convert === 'function'
    );
  }

  function isLandingPriceConvertible(currency) {
    if (!currency) return false;
    if (!ratesReady()) return false;
    var code = String(currency).toUpperCase();
    var rate = window.Currency.rates[code];
    return typeof rate === 'number' && isFinite(rate) && rate > 0;
  }

  function resolveTargetCurrency(explicit) {
    if (explicit) return String(explicit).toUpperCase();
    var stored = null;
    try {
      stored = sessionStorage.getItem('currency_base') || sessionStorage.getItem('currency');
    } catch (e) {
      stored = null;
    }
    if (stored) return String(stored).toUpperCase();
    if (typeof window.Shopify !== 'undefined' && window.Shopify.currency && window.Shopify.currency.active) {
      return String(window.Shopify.currency.active).toUpperCase();
    }
    return BASE_CURRENCY;
  }

  function convertAmount(amount, fromCurrency, toCurrency) {
    if (fromCurrency === toCurrency) return amount;
    if (!ratesReady()) return amount;
    if (!isLandingPriceConvertible(toCurrency)) return amount;
    if (!isLandingPriceConvertible(fromCurrency)) return amount;
    try {
      return window.Currency.convert(amount, fromCurrency, toCurrency);
    } catch (e) {
      return amount;
    }
  }

  // AB#9303 — force the currency symbol BEFORE the amount regardless of locale.
  // `Intl.NumberFormat({style:'currency'})` puts the symbol AFTER the number for
  // es/es-ES/de/fr/it (e.g. "47.266 $", "14,49 $") — the exact bug reported on
  // /es/ landings. We keep the locale's digit grouping / decimal separator (so
  // "es" still renders "47.266" / "14,49") but rebuild the string as
  // symbol + number, so it always reads "$47.266" / "$14,49" like en-US.
  function formatSymbolFirst(value, currency, locale, fractionDigits) {
    var parts = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits
    }).formatToParts(value);
    var sign = '';
    var symbol = '';
    var number = '';
    for (var i = 0; i < parts.length; i++) {
      var part = parts[i];
      if (part.type === 'currency') {
        symbol = part.value;
      } else if (part.type === 'minusSign' || part.type === 'plusSign') {
        sign = part.value;
      } else if (part.type !== 'literal') {
        // integer, group, decimal, fraction. `literal` (the locale spacing
        // between number and symbol, e.g. the " " in "47.266 $") is dropped —
        // we place the symbol ourselves.
        number += part.value;
      }
    }
    return sign + symbol + number;
  }

  function formatOne(span, targetCurrency) {
    var amountAttr = span.getAttribute('data-amount');
    var amount = parseFloat(amountAttr);
    var fromCurrency = (span.getAttribute('data-currency') || BASE_CURRENCY).toUpperCase();

    if (!isFinite(amount)) {
      span.setAttribute('data-formatted', targetCurrency);
      return;
    }

    var effectiveTarget = isLandingPriceConvertible(targetCurrency)
      ? targetCurrency
      : BASE_CURRENCY;

    var converted = convertAmount(amount, fromCurrency, effectiveTarget);
    var locale = getLocale();
    var fractionDigits = effectiveTarget === 'COP' || effectiveTarget === 'CLP' || effectiveTarget === 'PYG' ? 0 : 2;

    try {
      // AB#8585 — symbol-only display, matching /availability and PDP.
      // AB#9303 — symbol ALWAYS before the amount, never after, regardless of
      // the request locale (formatSymbolFirst rebuilds symbol + number).
      span.textContent = formatSymbolFirst(converted, effectiveTarget, locale, fractionDigits);
    } catch (e) {
      span.textContent = Number(converted).toFixed(fractionDigits);
    }

    span.setAttribute('data-amount', String(converted));
    span.setAttribute('data-currency', effectiveTarget);
    span.setAttribute('data-formatted', effectiveTarget);
  }

  // AB#8042 — when we cannot convert to the requested currency we fall back to
  // BASE_CURRENCY for the cards. The header selector still reflects the old
  // currency, so the user sees "COP" in the badge while the cards show USD.
  // To keep the page coherent we mirror the fallback into sessionStorage,
  // Shopify.currency.active and the visible header badge / dropdown highlight
  // — same surface that countries-currency.liquid:syncHeaderBadge updates on
  // a manual currency change.
  function syncHeaderToBaseCurrency() {
    try { sessionStorage.setItem('currency_base', BASE_CURRENCY); } catch (e) { /* private mode */ }
    try { sessionStorage.setItem('currency', BASE_CURRENCY); } catch (e) { /* private mode */ }
    if (typeof window.Shopify !== 'undefined' && window.Shopify.currency) {
      window.Shopify.currency.active = BASE_CURRENCY;
    }
    var labels = document.querySelectorAll('.localization_text');
    for (var i = 0; i < labels.length; i++) {
      labels[i].textContent = BASE_CURRENCY;
    }
    var items = document.querySelectorAll('#currency_container .item-currency');
    for (var j = 0; j < items.length; j++) {
      var isBase = items[j].id === BASE_CURRENCY;
      items[j].classList.toggle('active_currency', isBase);
      items[j].classList.toggle('selected', isBase);
    }
    window.dispatchEvent(new CustomEvent('hola:currency-changed', {
      detail: { currency: BASE_CURRENCY, reason: 'landing-price-fallback' }
    }));
  }

  function formatAll(targetCurrency) {
    var target = resolveTargetCurrency(targetCurrency);

    if (!ratesReady()) {
      pendingTarget = target;
      schedulePoll();
      return;
    }

    // AB#8042 — rates are ready but the requested currency is not convertible
    // (no rate, rate <= 0, etc.). Force USD coherently across cards + header.
    if (target !== BASE_CURRENCY && !isLandingPriceConvertible(target)) {
      syncHeaderToBaseCurrency();
      target = BASE_CURRENCY;
    }

    var spans = document.querySelectorAll('.landing-price[data-amount]');
    for (var i = 0; i < spans.length; i++) {
      var span = spans[i];
      var alreadyFormatted = span.getAttribute('data-formatted');
      if (alreadyFormatted === target) continue;
      formatOne(span, target);
    }

    window.dispatchEvent(new CustomEvent('hola:landing-prices-formatted', {
      detail: { currency: target }
    }));
  }

  function schedulePoll() {
    if (!pollStart) pollStart = Date.now();
    setTimeout(function () {
      if (ratesReady() && pendingTarget) {
        var target = pendingTarget;
        pendingTarget = null;
        pollStart = 0;
        formatAll(target);
        return;
      }
      if (Date.now() - pollStart < POLL_TIMEOUT_MS) {
        schedulePoll();
        return;
      }
      // Timeout — give up gracefully. CSS guard keeps spans hidden, but we
      // unhide them in BASE currency so the page is not stuck blank.
      // AB#8042 — also force the header badge / sessionStorage / Shopify.currency
      // to USD so the selector does not stay on COP/EUR/etc. while cards show USD.
      pendingTarget = null;
      pollStart = 0;
      syncHeaderToBaseCurrency();
      var spans = document.querySelectorAll('.landing-price[data-amount]:not([data-formatted])');
      for (var i = 0; i < spans.length; i++) {
        formatOne(spans[i], BASE_CURRENCY);
      }
      // eslint-disable-next-line no-console
      console.warn('[landing-price-format] window.Currency timeout — fell back to USD');
    }, POLL_INTERVAL_MS);
  }

  window.HolaLandingPriceFormat = {
    formatAll: formatAll,
    isLandingPriceConvertible: isLandingPriceConvertible
  };
  // Legacy alias used by countries-currency.liquid:110
  window.HolaLandingPrice = {
    isLandingPriceConvertible: isLandingPriceConvertible
  };

  // AB#8042 — header.liquid:619 mutates `currency_base` from the IP geolocation
  // response asynchronously, AFTER landing-price-format.js has already run and
  // formatted the cards in USD (cold cache, no `currency_base` yet). It then
  // dispatches `theme::currency::change` on `document.body` but no listener
  // re-formats the landing-price spans, so the header badge swaps to EUR/COP
  // while the cards stay on USD — the exact incongruence reported. Bridging
  // that event back into `formatAll` reuses the existing convertibility guard
  // and `syncHeaderToBaseCurrency()` fallback for non-convertible currencies.
  document.body.addEventListener('theme::currency::change', function (ev) {
    var currency = ev && ev.detail && ev.detail.currency;
    if (!currency) return;
    formatAll(currency);
  });

  function boot() {
    formatAll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
